/**
 * GradientIconContainer Component
 *
 * A reusable component for icon containers with gradient backgrounds.
 * Replaces patterns like `<Center className="...bg-gradient-to-br...">`.
 *
 * @example
 * ```tsx
 * <GradientIconContainer size="lg" variant="primary">
 *   <Icon icon={Cookie} size={8} color="white" />
 * </GradientIconContainer>
 * ```
 *
 * @see docs/ui-contract.md
 */

import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '@/shared/utils/utils';
import {Center} from './Center';

// =============================================================================
// VARIANTS
// =============================================================================

const gradientIconContainerVariants = cva(
  'mx-auto flex items-center justify-center',
  {
    variants: {
      /**
       * Size of the icon container
       */
      size: {
        sm: 'h-10 w-10 sm:h-12 sm:w-12',
        md: 'h-12 w-12 sm:h-14 sm:w-14',
        lg: 'h-14 w-14 sm:h-16 sm:w-16',
        xl: 'h-16 w-16',
      },
      /**
       * Shape of the container
       */
      shape: {
        rounded: 'rounded-xl sm:rounded-2xl',
        pill: 'rounded-full',
      },
      /**
       * Gradient color variant
       */
      variant: {
        primary: 'bg-gradient-to-br from-[hsl(var(--primary-color))] to-[hsl(var(--accent-color))]',
        accent: 'bg-gradient-to-br from-[hsl(var(--accent-color))] to-[hsl(var(--primary-color))]',
        // Subtle/glass variant with opacity
        subtle: 'bg-gradient-to-br from-primary/20 to-accent/20',
        // Custom gradient variants for TermsOfService
        warm: 'bg-gradient-to-br from-[hsl(var(--warm-color))] to-[hsl(var(--accent-color))]',
        cool: 'bg-gradient-to-br from-[hsl(var(--primary-color))] to-[hsl(var(--cool-color))]',
        secondary: 'bg-gradient-to-br from-[hsl(var(--secondary-color))] to-[hsl(var(--primary-color))]',
        warning: 'bg-gradient-to-br from-[hsl(var(--accent-color))] to-[hsl(var(--warm-color))]',
      },
    },
    defaultVariants: {
      size: 'md',
      shape: 'rounded',
      variant: 'primary',
    },
  }
);

// =============================================================================
// TYPES
// =============================================================================

interface GradientIconContainerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof gradientIconContainerVariants> {
  /**
   * Icon content to display
   */
  children: React.ReactNode;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * GradientIconContainer - Icon container with gradient background
 *
 * Use this component for icon containers that need gradient backgrounds.
 * This replaces patterns like:
 * - `<Center className="...bg-gradient-to-br from-primary to-accent...">`
 * - `<div className="...bg-gradient-to-br...">`
 */
export function GradientIconContainer({
  size,
  shape,
  variant,
  className,
  children,
  ...props
}: GradientIconContainerProps): React.ReactElement {
  return (
    <Center
      className={cn(
        gradientIconContainerVariants({ size, shape, variant }),
        className
      )}
      {...props}
    >
      {children}
    </Center>
  );
}

GradientIconContainer.displayName = 'GradientIconContainer';
